// Multi-page guide: creating a project from scratch.
//
// Two real route changes make this a genuine cross-page journey:
//   /project/list  -> Add New -> /project/new
//   submit         -> /project/:id/edit
const createProjectGuide = {
  id: 'create-project',
  kind: 'interactive',
  title: 'Create a new project',
  summary: 'Set up a project, link it to Revit, add your team and save.',
  version: 1,
  stages: [
    {
      id: 'list',
      match: '/dashboard/yawn/project/list',
      // Each anchor carries its own wording, so the hint matches whatever the
      // user can actually see. First visible one wins.
      waitingHint: 'Open the Projects page to begin.',
      waitingAnchors: [
        { target: 'projects-add', hint: 'Click "Add New" to start a project.' },
        { target: 'dashboard-new-project', hint: 'Click "New Project" to begin.' },
        { target: 'nav-Projects', hint: 'Open Projects from the menu to begin.' },
      ],
      steps: [
        {
          target: 'projects-add',
          title: 'Start a new project',
          body: 'Click "Add New" to open the project setup page.',
          placement: 'left',
        },
      ],
    },
    {
      id: 'create',
      match: '/dashboard/yawn/project/new',
      waitingHint: 'Open the project setup page to carry on.',
      waitingAnchors: [
        { target: 'projects-add', hint: 'Click "Add New" to open the project setup page.' },
        { target: 'dashboard-new-project', hint: 'Click "New Project" to open the setup page.' },
        { target: 'nav-Projects', hint: 'Open Projects from the menu, then click "Add New".' },
      ],
      steps: [
        {
          // One anchor over the whole block -- name, architect and region --
          // rather than stepping through each field.
          target: 'project-new-fields',
          title: 'Fill in the basics',
          body: 'Fill in the project name and region, plus any other details shown. The name is usually the building or site.',
          // Above the block: the Architect and Region fields open dropdowns
          // downwards, which a popover underneath would cover.
          placement: 'top',
        },
        {
          target: 'project-new-submit',
          title: 'Create it',
          body: 'Fill in the remaining details, then click "Create Project" to open it.',
          placement: 'top',
        },
      ],
    },
    {
      id: 'edit',
      // The project opens at /project/:id/edit once created.
      match: '/dashboard/yawn/project/,!/dashboard/yawn/project/list,!/dashboard/yawn/project/new',
      waitingHint: 'Open your new project to carry on.',
      waitingAnchors: [
        { target: 'nav-Projects', hint: 'Open Projects from the menu, then open your project.' },
      ],
      steps: [
        {
          target: 'project-code',
          title: 'Give it a code',
          body: 'A short project code, used as a prefix throughout your schedules and exports.',
          placement: 'bottom',
          optional: true,
        },
        {
          target: 'project-revit-number',
          title: 'Link it to Revit',
          body: 'Add your Revit project number here and the project appears in the EarlyPlugin projects page.',
          tip: 'Find it in Revit under Manage \u2192 Settings \u2192 Project Information.',
          placement: 'bottom',
          optional: true,
        },
        {
          target: 'project-details-section',
          title: 'Project details',
          body: 'Everything identifying the project lives here \u2014 name, code, region, Revit link and notes for your team.',
          placement: 'top',
          optional: true,
        },
        {
          target: 'project-additional-info',
          title: 'Additional information',
          body: 'Further project detail that carries through to your schedules and exports.',
          placement: 'top',
          optional: true,
        },
        {
          target: 'project-members',
          title: 'Members',
          body: 'Add people to the project and set what each of them can do.',
          tip: 'They need a company and a user account first \u2014 see Companies and Users in the main menu.',
          placement: 'top',
          optional: true,
        },
        {
          target: 'project-program',
          title: 'Program',
          body: 'Track the project timeline and key dates alongside the specification.',
          placement: 'left',
          optional: true,
        },
        {
          target: 'project-paragraph-numbering',
          title: 'Paragraph numbering',
          body: 'Set how clauses are numbered so your exports match your office standard.',
          placement: 'top',
          optional: true,
        },
        {
          target: 'project-additional-settings',
          title: 'Additional settings',
          body: 'Print headers and footers, and the rest of the per-project options.',
          placement: 'top',
          optional: true,
        },
        {
          target: 'project-save-changes',
          title: 'Save or discard',
          body: 'Click "Save Changes" to keep what you have set up, or "Cancel" to discard it. Either way your project is ready.',
          placement: 'top',
          // The unsaved-changes bar only appears once something has been
          // edited, so this is resolved when the user reaches it: shown if
          // there is anything to save, and the guide ends there if not.
          deferred: true,
        },
      ],
    },
  ],
};

export default createProjectGuide;
